import { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { employeeService } from '@/api/services/employeeService';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [employee, setEmployee] = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    let subscription = null;

    const initializeAuth = async () => {
      try {
        console.log('[AuthContext] Starting auth initialization...');
        // Add timeout to prevent infinite loading
        const sessionPromise = supabase.auth.getSession();
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Session timeout')), 10000)
        );

        const { data: { session: initialSession }, error: sessionError } = await Promise.race([
          sessionPromise,
          timeoutPromise
        ]);
        
        console.log('[AuthContext] Session check complete');

        if (sessionError) {
          console.error('Session error:', sessionError);
        }

        if (mounted) {
          console.log('[AuthContext] Setting session and user state');
          setSession(initialSession);
          setUser(initialSession?.user ?? null);

          if (initialSession?.user) {
            console.log('[AuthContext] User found, loading employee data');
            await loadEmployeeData(initialSession.user.email);
          }

          setLoading(false);
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        if (mounted) {
          setLoading(false);
        }
      }
    };

    const setupAuthListener = () => {
      const { data: authListener } = supabase.auth.onAuthStateChange(
        async (event, currentSession) => {
          if (!mounted) return;

          console.log('Auth state changed:', event);

          setSession(currentSession);
          setUser(currentSession?.user ?? null);

          if (currentSession?.user && event !== 'SIGNED_IN') {
            await loadEmployeeData(currentSession.user.email);
          } else if (!currentSession?.user) {
            setEmployee(null);
          }
        }
      );
      subscription = authListener.subscription;
    };

    initializeAuth();
    setupAuthListener();

    return () => {
      mounted = false;
      if (subscription) {
        subscription.unsubscribe();
      }
    };
  }, []);

  const loadEmployeeData = async (email) => {
    try {
      console.log('Loading employee data for email:', email);

      await new Promise(resolve => setTimeout(resolve, 100));

      const { data, error } = await supabase
        .from('employees')
        .select('*')
        .eq('email', email)
        .eq('is_active', true)
        .maybeSingle();

      console.log('Employee query result:', { data, error });

      if (error) {
        console.error('Error loading employee:', error);
        throw error;
      }

      if (data) {
        console.log('Employee data loaded:', data.employee_id, data.role);
        setEmployee(data);
      } else {
        console.warn('No employee record found for user:', email);
        setEmployee(null);
      }
    } catch (error) {
      console.error('Failed to load employee data:', error);
      setEmployee(null);
    }
  };

  const signInWithPin = async (employeeId, pin) => {
    try {
      const employeeData = await employeeService.getByEmployeeId(employeeId.trim());

      if (!employeeData || employeeData.pin !== pin.trim()) {
        throw new Error('Invalid employee ID or PIN');
      }

      if (!employeeData.is_active) {
        throw new Error('Account is inactive');
      }

      const password = `SJ${pin.trim()}PIN`;

      console.log('Signing in with email:', employeeData.email);
      console.log('Employee data being set:', employeeData);
      setEmployee(employeeData);

      const { data, error } = await supabase.auth.signInWithPassword({
        email: employeeData.email,
        password: password,
      });

      if (error) {
        if (error.message.includes('Invalid login credentials')) {
          const { error: signUpError } = await supabase.auth.signUp({
            email: employeeData.email,
            password: password,
            options: {
              data: {
                employee_id: employeeData.employee_id,
                full_name: employeeData.full_name,
              }
            }
          });

          if (signUpError) throw signUpError;

          const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
            email: employeeData.email,
            password: password,
          });

          if (signInError) throw signInError;
          return signInData;
        }
        throw error;
      }

      return data;
    } catch (error) {
      console.error('PIN sign-in error:', error);
      throw error;
    }
  };

  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;

      setEmployee(null);
      setUser(null);
      setSession(null);
    } catch (error) {
      console.error('Sign-out error:', error);
      throw error;
    }
  };

  const updatePin = async (newPin) => {
    try {
      if (!user) throw new Error('No authenticated user');

      const password = `SJ${newPin}PIN`;

      const { error } = await supabase.auth.updateUser({
        password: password
      });

      if (error) throw error;

      if (employee) {
        await employeeService.update(employee.id, { pin: newPin });
      }

      return true;
    } catch (error) {
      console.error('PIN update error:', error);
      throw error;
    }
  };

  const value = {
    user,
    employee,
    session,
    loading,
    signInWithPin,
    signOut,
    updatePin,
    isAuthenticated: !!user && !!employee,
    isAdmin: employee?.role === 'admin',
    isTrainer: employee?.role === 'trainer' || employee?.role === 'admin',
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
